/** Max characters for package weight/dimension measure fields (OLS/ESN MaxLength=6). */
export const PACKAGE_MEASURE_MAX_LENGTH = 6;

/** Max digits allowed after the decimal point. */
export const PACKAGE_MEASURE_MAX_DECIMALS = 2;

export interface PackageMeasureSelection {
  start: number;
  end: number;
}

export interface PackageMeasureKeyEvent {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

/**
 * Sanitizes a raw measure string to ESN/OM typing rules:
 * digits and at most one `.`, up to two decimal places, max 6 characters; strips negatives and other symbols.
 */
export function sanitizePackageMeasureInput(raw: string): string {
  let out = '';
  let seenDot = false;
  let decimals = 0;

  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      if (seenDot) {
        if (decimals >= PACKAGE_MEASURE_MAX_DECIMALS) {
          continue;
        }
        decimals += 1;
      }
      out += ch;
    } else if (ch === '.' && !seenDot) {
      seenDot = true;
      out += ch;
    }

    if (out.length >= PACKAGE_MEASURE_MAX_LENGTH) {
      break;
    }
  }

  return out;
}

/**
 * Returns true when a keydown should be accepted for a package measure field.
 * Allows navigation/editing shortcuts; blocks invalid characters and over-limit digits/decimals.
 */
export function isPackageMeasureKeyAllowed(
  event: PackageMeasureKeyEvent,
  currentValue: string,
  selection: PackageMeasureSelection,
): boolean {
  const { key, ctrlKey, metaKey, altKey } = event;

  if (ctrlKey || metaKey) {
    return true;
  }

  if (altKey) {
    return false;
  }

  if (
    key === 'Backspace' ||
    key === 'Delete' ||
    key === 'Tab' ||
    key === 'Escape' ||
    key === 'Enter' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'Home' ||
    key === 'End'
  ) {
    return true;
  }

  if (key.length !== 1) {
    return true;
  }

  const isDigit = key >= '0' && key <= '9';
  if (!isDigit && key !== '.') {
    return false;
  }

  const start = Math.max(0, Math.min(selection.start, currentValue.length));
  const end = Math.max(start, Math.min(selection.end, currentValue.length));
  const nextValue = `${currentValue.slice(0, start)}${key}${currentValue.slice(end)}`;

  return sanitizePackageMeasureInput(nextValue) === nextValue;
}
